import { ArchivedDocumentWithDetails, FileType } from "@/types";

export function base64ToFile(file: ArchivedDocumentWithDetails, filename: string): File {
  if (!file || !file.document) {
    return new File([], filename, { type: "application/octet-stream" });
  }

  const byteString = atob(file.document);
  const byteArray = new Uint8Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }

  return new File([byteArray], filename, { type: file.mediaType });
}

export function fileToBase64Raw(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      } else {
        reject(new Error("Failed to convert file to base64."));
      }
    };

    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(file);
  });
}

export const convertBytesToMb = (bytes: number) => (bytes / 1024 / 1024).toFixed(2);

export const getFileSizeInBytes = (base64String: string) => {
  if (!base64String) return 0;
  const base64Data = base64String?.split(",")[1] || base64String;

  const base64Length = base64Data.length;
  const padding = base64Data.endsWith("==") ? 2 : base64Data.endsWith("=") ? 1 : 0;

  const fileSizeInBytes = base64Length * 0.75 - padding;

  return Math.floor(fileSizeInBytes);
};

export const createURLFromBase64 = (base64String: string, mimeType: any) => {
  const base64Data = base64String.split(",")[1] || base64String;
  const binaryString = atob(base64Data);

  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: mimeType });

  return URL.createObjectURL(blob);
};

export const getMimeType = (fileType: FileType): string => {
  const mimeTypes: Record<FileType, string> = {
    PDF: "application/pdf",
    XML: "application/xml",
    JPG: "image/jpeg",
    JPEG: "image/jpeg",
    PNG: "image/png",
    DOC: "application/msword",
    DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    XLS: "application/vnd.ms-excel",
    XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    TXT: "text/plain",
  };

  return mimeTypes[fileType] || "application/octet-stream";
};

export function getFileTypeFromFile(file: File): FileType {
  const fileType = file.type;

  let extractedFileType: FileType;
  switch (fileType) {
    case "image/jpeg":
      extractedFileType = "JPEG";
      break;
    case "image/png":
      extractedFileType = "PNG";
      break;
    case "image/jpg":
      extractedFileType = "JPG";
      break;
    case "application/pdf":
      extractedFileType = "PDF";
      break;
    // case 'application/msword':
    // case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    //   extractedFileType = 'WORD';
    //   break;
    // case 'application/vnd.ms-excel':
    // case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    //   extractedFileType = 'EXCEL';
    //   break;
    default:
      extractedFileType = "PDF";
  }

  return extractedFileType;
}

/**
 * Creates a blob URL from an ArrayBuffer and opens it in a new tab.
 * The blob URL is automatically cleaned up after the tab opens.
 *
 * @param arrayBuffer - The binary data as an ArrayBuffer
 * @param contentType - The MIME type of the content (defaults to "application/pdf")
 */
export const openBlobInNewTab = (arrayBuffer: ArrayBuffer, contentType: string = "application/pdf") => {
  const blob = new Blob([arrayBuffer], { type: contentType });

  const blobUrl = URL.createObjectURL(blob);

  window.open(blobUrl, "_blank");

  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
};

/** Longest side of an uploaded photo, in pixels. */
const MAX_PHOTO_SIDE = 1920;
const PHOTO_QUALITY = 0.8;

/**
 * Re-encodes a photo as a JPEG no larger than 1920 px on its longest side, so phone photos (often several MB each)
 * stay small when sent base64-encoded in a JSON body. Falls back to the original file when the browser can't decode it.
 */
export const compressImage = async (file: File): Promise<File> => {
  const canvas = document.createElement("canvas");
  let bitmap: ImageBitmap | undefined;
  try {
    // Upright by its EXIF orientation: re-encoding drops that tag, so a sideways decode would stay sideways.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(bitmap.width, bitmap.height));
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext("2d");
    if (!context) return file;
    // JPEG has no transparency: paint PNGs onto white rather than black.
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", PHOTO_QUALITY));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    bitmap?.close();
    // Frees the canvas now rather than at garbage collection: iOS caps the memory all canvases may use.
    canvas.width = 0;
    canvas.height = 0;
  }
};
